import { Controller } from "@hotwired/stimulus";

/**
 * Adds loading state to form submit buttons.
 * Disables the button and shows a spinner on form submission.
 * Re-enables if there's an error (e.g., Turbo returns an error response).
 *
 * Usage:
 *   <form data-controller="form-submit">
 *     <button type="submit" data-form-submit-target="button">
 *       Submit
 *     </button>
 *   </form>
 *
 * Or with custom loading text:
 *   <button type="submit"
 *           data-form-submit-target="button"
 *           data-loading-text="Signing in...">
 *     Sign in
 *   </button>
 */
export default class extends Controller {
  static targets = ["button"];

  declare readonly buttonTarget: HTMLButtonElement;
  declare readonly hasButtonTarget: boolean;

  private originalContent: string = "";
  private isSubmitting: boolean = false;

  connect(): void {
    this.element.addEventListener("submit", this.handleSubmit.bind(this));

    // Listen for Turbo error responses to re-enable the button
    document.addEventListener(
      "turbo:before-fetch-response",
      this.handleTurboResponse.bind(this)
    );

    // Also handle page renders (which happen on validation errors)
    document.addEventListener(
      "turbo:render",
      this.handleTurboRender.bind(this)
    );
  }

  disconnect(): void {
    document.removeEventListener(
      "turbo:before-fetch-response",
      this.handleTurboResponse.bind(this)
    );
    document.removeEventListener(
      "turbo:render",
      this.handleTurboRender.bind(this)
    );
  }

  handleSubmit(event: Event): void {
    if (!this.hasButtonTarget || this.isSubmitting) return;

    this.isSubmitting = true;
    this.originalContent = this.buttonTarget.innerHTML;

    const loadingText =
      this.buttonTarget.dataset.loadingText || "Please wait...";

    // Add loading state
    this.buttonTarget.disabled = true;
    this.buttonTarget.innerHTML = `
      <span class="loading loading-spinner loading-sm"></span>
      <span>${loadingText}</span>
    `;
  }

  handleTurboResponse(event: CustomEvent): void {
    // Check if response indicates an error (4xx or 5xx)
    const response = event.detail?.fetchResponse?.response;
    if (response && !response.ok) {
      this.resetButton();
    }
  }

  handleTurboRender(): void {
    // Reset button after any Turbo render (handles validation errors)
    this.resetButton();
  }

  resetButton(): void {
    if (!this.hasButtonTarget || !this.isSubmitting) return;

    this.isSubmitting = false;
    this.buttonTarget.disabled = false;
    if (this.originalContent) {
      this.buttonTarget.innerHTML = this.originalContent;
    }
  }
}
